import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import React from 'react';
import { useEpdProductPanelContent, useGetLCASpecification } from 'services/api/queries';
import { HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { EPDContentDeclarationProductType } from 'types/types';

import { columnTotal, getBiogenicCarbonWeightHeaderPdf, getBiogenicCO2WeightHeaderPdf } from '../GridService';
import { DataTableStyled } from '../StyledGrid';

const header = (
  <div className="flex flex-wrap align-items-center justify-content-between gap-2">
    <HeaderCaptionSemiBold className="white-space-nowrap">Panel content</HeaderCaptionSemiBold>
  </div>
);

const PanelContentGridPdf: React.FunctionComponent<{
  epdVersionId: string;
  contentDeclarationType: EPDContentDeclarationProductType;
  isISO21930StandardSelected?: boolean;
}> = ({ epdVersionId, contentDeclarationType, isISO21930StandardSelected }) => {
  const rows = useEpdProductPanelContent(epdVersionId, contentDeclarationType).data;
  const lcaSpecification = useGetLCASpecification(epdVersionId!).data;

  if ((!rows || rows.length === 0) && contentDeclarationType !== EPDContentDeclarationProductType.Average) return null;

  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column className="p-row-total" footer="Total" />
        <Column className="p-row-total" footer={columnTotal('weight', rows)} />
        <Column className="p-row-total" footer={columnTotal('weightPercent', rows)} />
        <Column className="p-row-total" footer={columnTotal('biogenicCarbonWeight', rows)} />
        {isISO21930StandardSelected && <Column className="p-row-total" footer={columnTotal('biogenicCO2Weight', rows)} />}
      </Row>
      <Row style={{ borderTop: 'none' }}>
        <Column className="p-row-note" footer={<div>Note 1</div>} />
        <Column
          className="p-row-note"
          footer={
            <>
              1 kg biogenic carbon is equivalent to 44/12 kg of CO<sub>2</sub>
            </>
          }
          colSpan={isISO21930StandardSelected ? 4 : 3}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <DataTableStyled
      header={header}
      footerColumnGroup={footerGroup}
      value={rows}
      dataKey="id"
      tableStyle={{ minWidth: '49rem' }}
      showGridlines
    >
      <Column field="name" header="Content name" />
      <Column field="weight" header="Weight, kg" />
      <Column field="weightPercent" header="Weight-% (versus the product)" />
      <Column field="biogenicCarbonWeight" header={getBiogenicCarbonWeightHeaderPdf(lcaSpecification?.epdBasedOnUnit)} />
      {isISO21930StandardSelected && (
        <Column field="biogenicCO2Weight" header={getBiogenicCO2WeightHeaderPdf(lcaSpecification?.epdBasedOnUnit)} />
      )}
    </DataTableStyled>
  );
};

export default PanelContentGridPdf;
